import React, { Fragment } from "react";
import _ from "lodash";
import { compose } from "redux";
import { connect } from "react-redux";
import moment from "moment";
import shortid from "shortid";
import IconButton from "@material-ui/core/IconButton";
import styled from "styled-components";
import CloseIcon from "@material-ui/icons/Close";
import { Dialog, DialogTitle, Grid } from "@material-ui/core";
import { withStyles } from "@material-ui/core/styles";
import { Formik } from "formik";
import { ASSET_CLASS, ASSET_TYPE, ZONING, METROPOLITAN_STATISTICAL_AREA, STATES, REGION, MARKET_VALUE_METHODOLOGY } from "constants/configItemParents";
import GeneralInfo from "./PropertyModalSections/GeneralInfo";
import LocationInfo from "./PropertyModalSections/LocationInfo";
import PropertyHistoryInfo from "./PropertyModalSections/PropertyHistoryInfo";
import DispositionInfo from "./PropertyModalSections/DispositionInfo";
import PrimaryButton from "../../../Global/Buttons/PrimaryButton";
import SecondaryButton from "../../../Global/Buttons/SecondaryButton";
import SimpleConfirmDialog from "../../../Global/ConfirmationDialogs/SimpleConfirmDialog";
import OwnershipSection from "./PropertyModalSections/OwnershipSection";

const FormGrid = styled.div`
  padding: 0 20px 20px 20px;
`;

const FullHeight = styled.div`
  min-height: 400px;
`;

const styles = () => ({
  dialogPaper: {
    "max-width": "815px",
    "font-size": "13px"
  },
  dialogTitle: {
    color: "blue",
    padding: "0 0 0 20px"
  }
});

class PropertyModal extends React.Component {
  constructor(props) {
    super(props);
    this.state = { isConfirmMetricsUpdateDialogOpen: false, canShowOwnershipErrors: false };
  }

  onCancel = () => {
    this.props.onClose();
  };

  closeModal = () => {
    this.props.onClose();
  };

  add = property => {
    const fakeId = `fake_${shortid.generate()}`;
    property.id = fakeId;
    this.props.onAdd(property);
    this.closeModal();
  };

  save = property => {
    this.props.onEdit(property);
    this.closeModal();
  };

  errorHandlingSubmit = (errors, handleSubmit) => {
    if(!this.state.canShowOwnershipErrors) this.setState({canShowOwnershipErrors: true});
    if (_.isEmpty(errors)) handleSubmit();
  };

  submitValues = (values, setSubmitting) => {
    const modifiedValues = { ...values };
    modifiedValues.appraisedValue = modifiedValues.appraisedValue ? modifiedValues.appraisedValue.toString().replace(/[^0-9.-]+/g, "") : "";
    modifiedValues.noi = modifiedValues.noi ? modifiedValues.noi.toString().replace(/[^0-9.-]+/g, "") : "";
    modifiedValues.noiActual = modifiedValues.noiActual ? modifiedValues.noiActual.toString().replace(/[^0-9.-]+/g, "") : "";
    modifiedValues.purchasePrice = modifiedValues.purchasePrice ? modifiedValues.purchasePrice.toString().replace(/[^0-9.-]+/g, "") : "";
    modifiedValues.salesPrice = modifiedValues.salesPrice ? modifiedValues.salesPrice.toString().replace(/[^0-9.-]+/g, "") : "";
    modifiedValues.marketValue = modifiedValues.marketValue ? modifiedValues.marketValue.toString().replace(/[^0-9.-]+/g, "") : "";

    if (!this.props.isEditing) {
      this.add(modifiedValues);
    } else {
      this.save(modifiedValues);
    }
    setSubmitting(false);
  };
  
  
getSumOfOwnershipPercentages = (records) => {
  let sum = 0;
  records.forEach(x=> {
      sum += x.ownershipPercentage;
  });
  return sum;
}

  areOwnershipRecordsValid = (records) => {
    records.forEach(record => {
      let basicCheck = record.principleOwnerId 
      && (!!record.ownershipPercentage || record.ownershipPercentage === 0) 
      && record.ownershipPercentage <= 1;
      record.isValid = !!basicCheck;
    });
    return records.every(x=>x.isValid);
  }

  validateOwnershipRecordsSum = (records) => {
    if(!records || records.length == 0) return true;
    let sumOfOwnershipPercentages = this.getSumOfOwnershipPercentages(records);
    return sumOfOwnershipPercentages == 1.0;
  }

  areOwnershipRecordsGoodToSubmit = (records) => {
    return this.validateOwnershipRecordsSum(records) && this.areOwnershipRecordsValid(records);
  }

  render() {
    const { isOpen, property, propertyConfigItems, isEditing, classes } = this.props;
    const assetTypeOptions = propertyConfigItems[ASSET_TYPE];
    const assetClassOptions = propertyConfigItems[ASSET_CLASS];
    const marketValueMethodologyOptions = propertyConfigItems[MARKET_VALUE_METHODOLOGY];
    const zoningOptions = propertyConfigItems[ZONING];
    const msaOptions = propertyConfigItems[METROPOLITAN_STATISTICAL_AREA];
    const stateOptions = propertyConfigItems[STATES];
    const regionOptions = propertyConfigItems[REGION];
    return (
      <>
        <Dialog
          id="realEstateModel"
          open={isOpen}
          onClose={this.closeModal}
          fullWidth
          maxWidth="md"
          classes={{ paper: classes.dialogPaper }}
          disableBackdropClick
        >
          <Grid container direction="row" alignItems="center" justify="space-between">
            <DialogTitle classes={{ root: classes.dialogTitle }}>{isEditing ? "Edit Property" : "New Property"}</DialogTitle>
            <IconButton style={{ marginRight: 16 }} onClick={this.closeModal}>
              <CloseIcon id="xButton" style={{ fontSize: 36 }} />
            </IconButton>
          </Grid>
          <FormGrid>
            <Formik
              initialValues={property}
              validateOnChange
              validate={values => {
                const { name, address, city, stateId, zip, assetTypeId } = values;
                const errors = {};
                if (!name) errors.name = "Required";
                if (!address) errors.address = "Required";
                if (!city) errors.city = "Required";
                if (!stateId) errors.stateId = "Required";
                if (!assetTypeId) errors.assetTypeId = "Required";
                if (!zip) errors.zip = "Required";
                if (values.noi && !values.noiAsOfDate) errors.noiAsOfDate = "Required when providing NOI";
                if (values.marketValue && !values.marketValueAsOfDate) errors.marketValueAsOfDate = "Required when providing a market value";
                if (values.appraisedValue && !values.appraisedValueAsOfDate) errors.appraisedValueAsOfDate = "Required when providing an appraised value";
                if (moment(values.noiAsOfDate).isAfter(moment())) errors.noiAsOfDate = "As of date cannot be in the future";
                if (moment(values.marketValueAsOfDate).isAfter(moment())) errors.marketValueAsOfDate = "As of date cannot be in the future";
                if (moment(values.appraisedValueAsOfDate).isAfter(moment())) errors.appraisedValueAsOfDate = "As of date cannot be in the future";
                if(!this.areOwnershipRecordsValid(values.ownershipRecords || [])){
                  errors.ownershipRecords = "Invalid Record(s)"
                }
                if(!this.validateOwnershipRecordsSum()){
                  errors.ownershipRecords = "Ownership Percentages Exceed 100%"
                }
                return errors;
              }}
              onSubmit={(values, { setSubmitting, validateForm }) => {
                if(!this.areOwnershipRecordsGoodToSubmit(values.ownershipRecords || [])) {
                  return null;
                }
                if (
                  (property.noiAsOfDate && values.noiAsOfDate && property.noiAsOfDate > values.noiAsOfDate) ||
                  (property.marketValueAsOfDate && values.marketValueAsOfDate && property.marketValueAsOfDate > values.marketValueAsOfDate) ||
                  (property.appraisedValueAsOfDate && values.appraisedValueAsOfDate && property.appraisedValueAsOfDate > values.appraisedValueAsOfDate)
                ) {
                  this.setState({ isConfirmMetricsUpdateDialogOpen: true, submitValues: values, setSubmitting });
                } else {
                  this.submitValues(values, setSubmitting);
                }
              }}
              render={({ values, errors, touched, handleChange, handleBlur, handleSubmit, validateForm }) => (
                <>
                  <FullHeight>
                    <GeneralInfo
                      handleChange={handleChange}
                      handleBlur={handleBlur}
                      touched={touched}
                      values={values}
                      errors={errors}
                      assetTypeOptions={assetTypeOptions}
                      assetClassOptions={assetClassOptions}
                      marketValueMethodologyOptions={marketValueMethodologyOptions}
                      zoningOptions={zoningOptions}
                    />
                    {values.assetTypeId && (
                      <LocationInfo
                        handleChange={handleChange}
                        handleBlur={handleBlur}
                        touched={touched}
                        values={values}
                        errors={errors}
                        msaOptions={msaOptions}
                        stateOptions={stateOptions}
                        regionOptions={regionOptions}
                      />
                    )}
                    {values.assetTypeId && (
                      <PropertyHistoryInfo handleChange={handleChange} handleBlur={handleBlur} errors={errors} touched={touched} values={values} />
                    )}
                     {values.assetTypeId &&
                    <OwnershipSection validateForm = {validateForm} canShowErrors = {this.state.canShowOwnershipErrors} handleChange={handleChange} handleBlur={handleBlur} errors={errors} touched={touched} values={values} />
                     }
                    {values.assetTypeId && (
                      <DispositionInfo handleChange={handleChange} handleBlur={handleBlur} values={values} touched={touched} errors={errors} />
                    )}
                  </FullHeight>
                  <Grid container direction="row" justify="flex-end">
                    <SecondaryButton id="cancelButton" onClick={this.onCancel}>
                      Cancel
                    </SecondaryButton>
                    {!isEditing && (
                      <PrimaryButton id="submitButton" onClick={handleSubmit}>
                        Add
                      </PrimaryButton>
                    )}
                    {isEditing && (
                      <PrimaryButton id="submitButton" onClick={() => this.errorHandlingSubmit(errors, handleSubmit)}>
                        Save
                      </PrimaryButton>
                    )}
                  </Grid>
                </>
              )}
            />
          </FormGrid>
        </Dialog>

        <SimpleConfirmDialog
          title="Confirm Update"
          message={(
            <div>
              You entered an “As Of Date” prior to the current entry in the database. This will overwrite historical metrics from the date entered until today.
              This will also update auto-calculated projections.
              <br />
              <br />
              Are you sure you want to do this?
            </div>
          )}
          noText="NO"
          yesText="YES, UPDATE"
          modalOpen={this.state.isConfirmMetricsUpdateDialogOpen}
          handleNo={() => this.setState({ isConfirmMetricsUpdateDialogOpen: false })}
          handleYes={() => {
            this.submitValues(this.state.submitValues, this.state.setSubmitting);
            this.setState({ isConfirmMetricsUpdateDialogOpen: false, submitValues: undefined, setSubmitting: undefined });
          }}
        />
      </>
    );
  }
}

function mapStateToProps() {
  return {};
}

export default compose(connect(mapStateToProps, {}), withStyles(styles))(PropertyModal);
